import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import localeUtils from 'utils/LanguageLocale';
import Location from 'utils/Location';
import { colors, radii } from 'style/config';
import {
    Container, Box, Flex, Grid, Text, Button, Divider, Link, Image
} from 'components/ui';
import StickyFooter from 'components/StickyFooter/StickyFooter';
import {
    getHeroContent,
    getContentSections,
    SHOP_ONLINE_URL,
    SHOP_MY_STORE_URL,
    MANAGE_LINKED_ACCOUNTS_URL,
    POINTS_HISTORY_URL,
    TERMS_URL
} from 'components/LoyaltyConnection/constants';
import LoyaltyConnectionEN from 'components/LoyaltyConnection/locales/LoyaltyConnection_en_US';
import LoyaltyConnectionFR from 'components/LoyaltyConnection/locales/LoyaltyConnection_fr_CA';

/**
 * Landing view for the Sephora x Starbucks loyalty partnership (LC-145).
 *
 * Bespoke layouts match the Figma designs per breakpoint AND per link state:
 *  - Unlinked (SUI 3201-94424 / LUI 3132-73718): hero with the Link CTA, then
 *    Perks, then "How It Works" steps, then terms. Small UI has a sticky
 *    "Get Started" CTA.
 *  - Linked (SUI 3201-94530 / LUI 3132-73036): hero with "Account linked", then
 *    "How Do I earn…" steps at the top (small UI adds Shop actions), then Perks,
 *    then manage/points/terms links. No "How It Works", no sticky CTA.
 *
 * Content (hero, perks, steps) is CMS-driven (getHeroContent/getContentSections);
 * CTA/linked-state/terms strings come from the locale resource files.
 */

// Perks card: white surface, hairline border, rounded, clipped image.
const PERK_CARD_CSS = {
    border: `1px solid ${colors.midGray}`,
    borderRadius: radii[2],
    overflow: 'hidden'
};

const LoyaltyConnectionLanding = ({ contentSections, isLinked, onLinkClick }) => {
    const getText = localeUtils.getLocalisedDataFromResource(LoyaltyConnectionEN, LoyaltyConnectionFR);

    const items = Array.isArray(contentSections) ? contentSections : [];
    const hero = getHeroContent(items);
    const sections = getContentSections(items);
    // Linked shows "How Do I earn…" (top); unlinked shows "How It Works" (bottom).
    const steps = isLinked ? sections.howToEarn : sections.howItWorks;

    const renderSectionTitle = (title, fontSize = 'md') =>
        title ? (
            <Text
                is='h2'
                fontWeight='bold'
                fontSize={fontSize}
                lineHeight='tight'
                marginBottom={4}
            >
                {title}
            </Text>
        ) : null;

    const renderNumberCircle = value => (
        <Flex
            width={44}
            height={44}
            flexShrink={0}
            borderRadius='full'
            backgroundColor='black'
            alignItems='center'
            justifyContent='center'
        >
            <Text
                color='nearWhite'
                fontWeight='bold'
                fontSize='xl'
                lineHeight='none'
            >
                {value}
            </Text>
        </Flex>
    );

    // Step visual: the CMS-provided icon (BannerList item media). Falls back to a
    // numbered circle only when the content item has no icon.
    const renderStepVisual = (card, index) =>
        card.image ? (
            <Image
                src={card.image}
                alt={card.title || ''}
                width={48}
                height={48}
                flexShrink={0}
                display='block'
            />
        ) : (
            renderNumberCircle(index + 1)
        );

    const renderCardText = card => (
        <Box>
            {card.title ? (
                <Text
                    is='p'
                    fontWeight='bold'
                    fontSize='base'
                    lineHeight='tight'
                    marginBottom={1}
                >
                    {card.title}
                </Text>
            ) : null}
            <Text
                is='p'
                fontSize='base'
                lineHeight='tight'
            >
                {card.description}
            </Text>
        </Box>
    );

    // Perks card: media on top, bold heading + body copy below.
    const renderPerkCard = (card, index, imageHeight, titleFontSize) => (
        <Box
            key={index}
            backgroundColor='white'
            css={PERK_CARD_CSS}
        >
            <Box
                height={imageHeight}
                backgroundColor='nearWhite'
            >
                {card.image ? (
                    <Image
                        src={card.image}
                        alt={card.title}
                        width='100%'
                        height='100%'
                        objectFit='cover'
                        display='block'
                    />
                ) : null}
            </Box>
            <Box
                paddingX={2}
                paddingTop={3}
                paddingBottom={3}
            >
                {card.title ? (
                    <Text
                        is='p'
                        fontWeight='bold'
                        fontSize={titleFontSize}
                        lineHeight='tight'
                        marginBottom={1}
                    >
                        {card.title}
                    </Text>
                ) : null}
                <Text
                    is='p'
                    fontSize='base'
                    lineHeight='tight'
                >
                    {card.description}
                </Text>
            </Box>
        </Box>
    );

    const renderPerksSection = large => {
        const perks = sections.perks;

        if (!perks?.cards.length) {
            return null;
        }

        return (
            <Box>
                {renderSectionTitle(perks.title, large ? 'lg-bg' : 'md')}
                <Grid
                    columns={large ? perks.cards.length : 1}
                    gap={large ? 5 : 4}
                >
                    {perks.cards.map((card, index) => renderPerkCard(card, index, large ? 147 : 125, large ? 'md' : 'base'))}
                </Grid>
            </Box>
        );
    };

    // Steps: number circle on top (large UI) or on the left (small UI) + copy.
    const renderStepsSection = large => {
        if (!steps?.cards.length) {
            return null;
        }

        return (
            <Box>
                {renderSectionTitle(steps.title, large ? 'lg-bg' : 'md')}
                <Grid
                    columns={large ? steps.cards.length : 1}
                    gap={3}
                >
                    {steps.cards.map((card, index) =>
                        large ? (
                            <Box
                                key={index}
                                backgroundColor='nearWhite'
                                borderRadius={2}
                                padding={5}
                            >
                                {renderStepVisual(card, index)}
                                <Box marginTop={3}>{renderCardText(card)}</Box>
                            </Box>
                        ) : (
                            <Flex
                                key={index}
                                backgroundColor='nearWhite'
                                borderRadius={2}
                                padding={4}
                                alignItems='center'
                            >
                                <Box marginRight={3}>{renderStepVisual(card, index)}</Box>
                                {renderCardText(card)}
                            </Flex>
                        )
                    )}
                </Grid>
            </Box>
        );
    };

    // Small-UI linked shop actions, shown between the steps and the perks.
    const renderShopActions = () => (
        <Box marginTop={4}>
            <Button
                variant='primary'
                width='100%'
                onClick={() => Location.setLocation(SHOP_ONLINE_URL)}
                data-at={Sephora.debug.dataAt('starbucks_shop_online')}
            >
                {getText('shopOnline')}
            </Button>
            <Link
                href={SHOP_MY_STORE_URL}
                display='block'
                color='link'
                textAlign='center'
                paddingY={4}
                data-at={Sephora.debug.dataAt('starbucks_shop_my_store')}
            >
                {getText('shopMyStore')}
            </Link>
        </Box>
    );

    // Full-width bottom links + terms body. Linked adds manage/points links.
    const renderBottomBlock = () => {
        const bottomLinks = [
            ...(isLinked
                ? [
                    { label: getText('manageLinkedAccounts'), url: MANAGE_LINKED_ACCOUNTS_URL },
                    { label: getText('seeAllPointsActivity'), url: POINTS_HISTORY_URL }
                ]
                : []),
            { label: getText('termsLinkLabel'), url: TERMS_URL, dataAt: 'starbucks_terms_link' }
        ].filter(link => link.label);

        return (
            <Box marginTop={5}>
                <Divider color='lightGray' />
                {bottomLinks.map(link => (
                    <React.Fragment key={link.label}>
                        <Link
                            href={link.url}
                            display='block'
                            color='link'
                            fontSize='base'
                            paddingY={4}
                            {...(link.dataAt && { 'data-at': Sephora.debug.dataAt(link.dataAt) })}
                        >
                            {link.label}
                        </Link>
                        <Divider color='lightGray' />
                    </React.Fragment>
                ))}
                {getText('termsBody') ? (
                    <Text
                        is='p'
                        fontSize='sm'
                        lineHeight='tight'
                        marginTop={4}
                    >
                        {getText('termsBody')}
                    </Text>
                ) : null}
            </Box>
        );
    };

    const renderLinkedStatus = marginProps => (
        <Text
            is='p'
            fontWeight='bold'
            color='green'
            {...marginProps}
            data-at={Sephora.debug.dataAt('starbucks_account_linked_status')}
        >
            &#10003; {getText('accountLinked')}
        </Text>
    );

    // ---------------------------------------------------------------- Small UI
    const smallUi = (
        <Container
            paddingTop={0}
            paddingBottom={isLinked ? 5 : 120}
        >
            {hero ? (
                <>
                    <Box
                        marginX='-container'
                        backgroundColor='nearWhite'
                        css={{ overflow: 'hidden' }}
                    >
                        {hero.mobileImage ? (
                            <Image
                                src={hero.mobileImage}
                                alt={hero.alt}
                                width='100%'
                                height={100}
                                objectFit='cover'
                                display='block'
                            />
                        ) : null}
                    </Box>
                    <Text
                        is='h1'
                        fontWeight='bold'
                        fontSize='lg'
                        lineHeight='tight'
                        marginTop={4}
                    >
                        {hero.heading}
                    </Text>
                </>
            ) : null}

            {isLinked ? renderLinkedStatus({ marginTop: 3 }) : null}

            <Divider
                color='lightGray'
                marginY={4}
            />

            {isLinked ? (
                <>
                    {renderStepsSection(false)}
                    {renderShopActions()}
                    <Box marginTop={5}>{renderPerksSection(false)}</Box>
                </>
            ) : (
                <>
                    {renderPerksSection(false)}
                    <Box marginTop={5}>{renderStepsSection(false)}</Box>
                </>
            )}

            {renderBottomBlock()}
        </Container>
    );

    // ---------------------------------------------------------------- Large UI
    const largeUi = (
        <Container
            paddingTop={6}
            paddingBottom={8}
        >
            {/* Hero: green banner image + white panel with headline and CTA/status. */}
            {hero ? (
                <Flex css={{ height: 167 }}>
                    <Box
                        flex={1}
                        backgroundColor='nearWhite'
                        css={{ overflow: 'hidden' }}
                    >
                        {hero.image ? (
                            <Image
                                src={hero.image}
                                alt={hero.alt}
                                width='100%'
                                height='100%'
                                objectFit='cover'
                                display='block'
                            />
                        ) : null}
                    </Box>
                    <Flex
                        flex={1}
                        flexDirection='column'
                        justifyContent='center'
                        backgroundColor='white'
                        paddingX={7}
                        css={{ border: `1px solid ${colors.lightGray}` }}
                    >
                        <Text
                            is='h1'
                            fontWeight='bold'
                            fontSize='lg'
                            lineHeight='tight'
                            css={{ maxWidth: 528 }}
                        >
                            {hero.heading}
                        </Text>
                        {isLinked ? (
                            renderLinkedStatus({ marginTop: 4 })
                        ) : (
                            <Box marginTop={4}>
                                <Button
                                    variant='primary'
                                    onClick={onLinkClick}
                                    data-at={Sephora.debug.dataAt('starbucks_link_cta')}
                                >
                                    {getText('ctaDesktop')}
                                </Button>
                            </Box>
                        )}
                    </Flex>
                </Flex>
            ) : null}

            {isLinked ? (
                <>
                    <Box marginTop={5}>{renderStepsSection(true)}</Box>
                    <Divider
                        color='lightGray'
                        marginY={5}
                    />
                    {renderPerksSection(true)}
                </>
            ) : (
                <>
                    <Box marginTop={5}>{renderPerksSection(true)}</Box>
                    <Divider
                        color='lightGray'
                        marginY={5}
                    />
                    {renderStepsSection(true)}
                </>
            )}

            {renderBottomBlock()}
        </Container>
    );

    return (
        <>
            <Box display={['block', 'none']}>{smallUi}</Box>
            <Box display={['none', 'block']}>{largeUi}</Box>

            {/* Sticky "Get Started" CTA — small UI, unlinked only. */}
            {isLinked ? null : (
                <Box display={['block', 'none']}>
                    <StickyFooter accountForBottomNav={true}>
                        <Button
                            variant='primary'
                            width='100%'
                            onClick={onLinkClick}
                            data-at={Sephora.debug.dataAt('starbucks_link_cta_sticky')}
                        >
                            {getText('ctaMobile')}
                        </Button>
                    </StickyFooter>
                </Box>
            )}
        </>
    );
};

export default wrapFunctionalComponent(LoyaltyConnectionLanding, 'LoyaltyConnectionLanding');
